// Memory-optimized deep clone with depth limiting and circular reference protection
const MAX_DEPTH = 10
const cloneCache = new WeakMap()

export default function deepClone(o: any, depth = 0): any {
  // Prevent infinite recursion
  if (depth > MAX_DEPTH) {
    console.warn('deepClone: Maximum depth reached, returning original object')
    return o
  }

  if (typeof o !== 'object' || o === null) {
    return o
  }

  // Handle circular references
  if (cloneCache.has(o)) {
    return cloneCache.get(o)
  }

  // https://jsperf.com/deep-copy-vs-json-stringify-json-parse/25
  if (Array.isArray(o)) {
    const newO: any[] = []
    cloneCache.set(o, newO)

    for (let i = 0; i < o.length; i += 1) {
      const val = !o[i] || typeof o[i] !== 'object' ? o[i] : deepClone(o[i], depth + 1)
      newO[i] = val === undefined ? null : val
    }
    return newO
  }

  const newO: any = {}
  cloneCache.set(o, newO)

  for (const i of Object.keys(o)) {
    const val = !o[i] || typeof o[i] !== 'object' ? o[i] : deepClone(o[i], depth + 1)
    if (val === undefined) {
      continue
    }
    newO[i] = val
  }

  // Clean up cache after cloning to prevent memory leaks
  if (depth === 0) {
    cloneCache.delete(o)
  }

  return newO
}
